import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
    Alert
} from '@mui/material';
import Grid from '@mui/material/Grid';
import SaveButton from '../Components/SaveButton';

const prepareVisibleCustomers = (customers, searchTerm) => customers
    .filter(customer => customer.searchName.includes(searchTerm))
    .sort((a, b) => {
        if (a.hasActiveProject !== b.hasActiveProject) {
            return a.hasActiveProject ? -1 : 1;
        }
        return (a.customer_name || '').localeCompare(b.customer_name || '');
    });

const CustomerAssignment = (props) => {
    const [customerList, setCustomerList] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [changed, setChanged] = useState(false);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [targetEmployee, setTargetEmployee] = useState(null);
    const [disassociateSource, setDisassociateSource] = useState(false);
    const [isAssociating, setIsAssociating] = useState(false);
    const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });

    const applyCustomersToEmployee = async (employeeId, customers, removeOtherCustomers = false, transferFromEmployeeId = null) => {
        const response = await axios.put(`${APIURL}/project/customer-assignments`, {
            comp_id: props.comp_id,
            emp_id: employeeId,
            customerIds: customers.map(customer => customer.id),
            removeOtherCustomers,
            source_emp_id: transferFromEmployeeId,
            disassociateSource: transferFromEmployeeId !== null
        });
        return response.data?.updateCount || 0;
    };

    useEffect(() => {
        async function fetchCustomers() {
            setIsLoadingCustomers(true);
            try {
                const response = await axios.get(APIURL + '/customers/getCustomersList', {
                    params: { company_id: props.comp_id, include_project_status: true }
                });
                const customers = Array.isArray(response.data?.customers) ? response.data.customers : [];
                setCustomerList(customers.map(customer => ({
                    id: customer.cus_id,
                    customer_name: customer.cus_name,
                    searchName: (customer.cus_name || '').toLowerCase(),
                    hasActiveProject: customer.has_active_project === true || customer.has_active_project === 1
                })));
            } catch (error) {
                console.error('Error fetching customers:', error);
                setCustomerList([]);
            } finally {
                setIsLoadingCustomers(false);
            }
        }

        fetchCustomers();
    }, [props.comp_id]);

    useEffect(() => {
        setEmployeeList([...(props.empList || [])].sort((a, b) =>
            (a.emp_first_name || '').localeCompare(b.emp_first_name || '')
        ));
    }, [props.empList]);

    const loadEmployeeAssignments = async (employee) => {
        setSelectedEmployee(employee);
        setTargetEmployee(null);
        setDisassociateSource(false);
        setSearchTerm('');
        setChanged(false);

        if (!employee) {
            setLeft([]);
            setRight([]);
            return;
        }

        setIsLoadingAssignments(true);
        try {
            const response = await axios.get(`${APIURL}/project/customer-assignments`, {
                params: { comp_id: props.comp_id, emp_id: employee.emp_id }
            });
            const assignedCustomerIds = new Set(
                (response.data?.customerIds || []).map(customerId => String(customerId))
            );

            setLeft(customerList.filter(customer => !assignedCustomerIds.has(String(customer.id))));
            setRight(customerList.filter(customer => assignedCustomerIds.has(String(customer.id))));
        } catch (error) {
            console.error('Error fetching employee customer assignments:', error);
            setLeft(customerList);
            setRight([]);
        } finally {
            setIsLoadingAssignments(false);
        }
    };

    const handleSave = async () => {
        if (!selectedEmployee || isSaving) return;

        setIsSaving(true);
        try {
            const updateCount = await applyCustomersToEmployee(selectedEmployee.emp_id, right, true);

            setChanged(false);
            setFeedback({
                open: true,
                message: `Customer assignments saved successfully (${updateCount} project update${updateCount === 1 ? '' : 's'})`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error saving assignments:', error);
            setFeedback({
                open: true,
                message: error.response?.data?.message || error.message || 'Failed to save customer assignments',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggle = (customer) => () => {
        if (!customer.hasActiveProject) return;
        setChanged(true);
        if (right.some(item => item.id === customer.id)) {
            setRight(current => current.filter(item => item.id !== customer.id));
            setLeft(current => [...current, customer]);
        } else {
            setLeft(current => current.filter(item => item.id !== customer.id));
            setRight(current => [...current, customer]);
        }
    };

    const handleAssociateWithEmployee = async () => {
        if (!selectedEmployee || !targetEmployee || right.length === 0 || isAssociating) return;

        setIsAssociating(true);
        try {
            const sourceEmployee = selectedEmployee;
            const updateCount = await applyCustomersToEmployee(
                targetEmployee.emp_id,
                right,
                false,
                disassociateSource ? sourceEmployee.emp_id : null
            );

            if (disassociateSource) {
                await loadEmployeeAssignments(sourceEmployee);
            }

            setFeedback({
                open: true,
                message: disassociateSource
                    ? `Selected customers transferred to ${targetEmployee.emp_first_name} ${targetEmployee.emp_last_name}`
                    : `${right.length} customer${right.length === 1 ? '' : 's'} associated with ${targetEmployee.emp_first_name} ${targetEmployee.emp_last_name} across ${updateCount} project${updateCount === 1 ? '' : 's'}`,
                severity: 'success'
            });
            setTargetEmployee(null);
        } catch (error) {
            console.error('Error associating customers with employee:', error);
            setFeedback({
                open: true,
                message: error.response?.data?.message || error.message || 'Failed to associate customers with employee',
                severity: 'error'
            });
        } finally {
            setIsAssociating(false);
        }
    };

    const normalizedSearchTerm = searchTerm.toLowerCase();
    const visibleLeft = useMemo(
        () => prepareVisibleCustomers([...left], normalizedSearchTerm),
        [left, normalizedSearchTerm]
    );
    const visibleRight = useMemo(
        () => prepareVisibleCustomers([...right], normalizedSearchTerm),
        [right, normalizedSearchTerm]
    );

    const customerListPanel = (title, customers, assigned) => (
        <Paper sx={{ width: 300 }}>
            <Typography variant="h6" align="center">{title}</Typography>
            <Divider />
            <List dense component="div" role="list" sx={{ overflow: 'auto', height: 270 }}>
                {customers.map((customer) => {
                    const labelId = `customer-assignment-${customer.id}-label`;
                    const disabled = !customer.hasActiveProject;
                    const item = (
                        <ListItem
                            key={customer.id}
                            role="listitem"
                            button
                            disabled={disabled}
                            onClick={handleToggle(customer)}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    checked={assigned}
                                    disabled={disabled}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': labelId }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                id={labelId}
                                primary={`${customer.customer_name} (${customer.id})`}
                            />
                        </ListItem>
                    );
                    return (
                        disabled
                            ? <Tooltip key={customer.id} title="This customer has no active project and cannot be assigned." placement="top" arrow>
                                <span>{item}</span>
                            </Tooltip>
                            : item
                    );
                })}
            </List>
        </Paper>
    );

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
                <Grid size={props.leftColumnSize}>
                    <Stack spacing={1}>
                        <Typography variant="h5">Customer Assignment</Typography>
                        <Typography variant="body1" sx={{ color: 'gray' }}>
                            Select an employee and manage their customer assignments
                        </Typography>
                    </Stack>
                </Grid>
                <Grid size={props.rightColumnSize}>
                    <Grid container spacing={2}>
                        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                            <Autocomplete
                                id="employee-select"
                                options={employeeList}
                                value={selectedEmployee}
                                getOptionLabel={(employee) =>
                                    `${employee.emp_first_name || ''} ${employee.emp_last_name || ''}`.trim()
                                }
                                onChange={(event, employee) => loadEmployeeAssignments(employee)}
                                renderInput={(params) =>
                                    <TextField {...params} label="Select Employee" margin="normal" />
                                }
                                fullWidth
                                size="small"
                                disabled={isLoadingCustomers || customerList.length === 0}
                            />
                            <TextField
                                label="Search Customers"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                fullWidth
                                margin="normal"
                                disabled={!selectedEmployee || isLoadingAssignments}
                                size="small"
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: '100%' }} my={1}>
                            {(isLoadingCustomers || isLoadingAssignments) && <CircularProgress size={20} />}
                            <Typography variant="body1" align="center">
                                {isLoadingCustomers
                                    ? 'Loading customers...'
                                    : isLoadingAssignments
                                    ? 'Loading customer assignments...'
                                    : selectedEmployee
                                        ? `Assigning customers to ${selectedEmployee.emp_first_name} ${selectedEmployee.emp_last_name}`
                                        : 'Select an employee to manage customer assignments'}
                            </Typography>
                        </Box>

                        <Grid
                            container
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                            aria-busy={isLoadingCustomers || isLoadingAssignments}
                            sx={{
                                opacity: isLoadingCustomers || isLoadingAssignments ? 0.55 : 1,
                                pointerEvents: isLoadingCustomers || isLoadingAssignments ? 'none' : 'auto',
                                transition: 'opacity 150ms ease-in-out'
                            }}
                        >
                            <Grid size={5}>{customerListPanel('Choose Clients', visibleLeft, false)}</Grid>
                            <Grid size={2}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const assignable = left.filter(customer => customer.hasActiveProject);
                                            setRight(current => [...current, ...assignable]);
                                            setLeft(current => current.filter(customer => !customer.hasActiveProject));
                                            if (assignable.length) setChanged(true);
                                        }}
                                        disabled={!selectedEmployee || !left.some(customer => customer.hasActiveProject) || isLoadingAssignments}
                                        sx={{ width: 100 }}
                                    >
                                        ≫
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            setLeft(current => [...current, ...right]);
                                            setRight([]);
                                            setChanged(true);
                                        }}
                                        disabled={!selectedEmployee || !right.length || isLoadingAssignments}
                                        sx={{ width: 100 }}
                                    >
                                        ≪
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid size={5}>{customerListPanel('Selected Clients', visibleRight, true)}</Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
                            <SaveButton onClick={handleSave} disabled={!changed || isLoadingAssignments || isSaving} loading={isSaving} />
                        </Box>

                        <Divider sx={{ width: '100%', my: 2 }} />

                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Associate all selected customers
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'gray', mb: 1 }}>
                                Associate all customers in the Assigned Clients list with another employee.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Autocomplete
                                    options={employeeList.filter(employee =>
                                        !selectedEmployee || String(employee.emp_id) !== String(selectedEmployee.emp_id)
                                    )}
                                    value={targetEmployee}
                                    getOptionLabel={(employee) =>
                                        `${employee.emp_first_name || ''} ${employee.emp_last_name || ''}`.trim()
                                    }
                                    onChange={(event, employee) => setTargetEmployee(employee)}
                                    renderInput={(params) =>
                                        <TextField {...params} label="Select Another Employee" />
                                    }
                                    disabled={!selectedEmployee || right.length === 0 || isAssociating}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAssociateWithEmployee}
                                    disabled={!targetEmployee || right.length === 0 || isAssociating}
                                >
                                    {isAssociating && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
                                    {isAssociating ? 'Associating...' : 'Associate Customers'}
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Checkbox
                                    checked={disassociateSource}
                                    onChange={(event) => setDisassociateSource(event.target.checked)}
                                    disabled={!selectedEmployee || !targetEmployee || right.length === 0 || isAssociating}
                                    inputProps={{ 'aria-label': 'Disassociate selected customers from the current employee' }}
                                />
                                <Typography variant="body2">
                                    Disassociate selected customers from the current employee after associating them with the new employee
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
            <Snackbar
                open={feedback.open}
                autoHideDuration={4000}
                onClose={() => setFeedback(current => ({ ...current, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={feedback.severity}
                    variant="filled"
                    onClose={() => setFeedback(current => ({ ...current, open: false }))}
                    sx={{ width: '100%' }}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CustomerAssignment;
